import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import { Shield, ArrowLeft, ArrowRight, Check, Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const STEPS = [
  { num: 1, title: 'Fund Details' },
  { num: 2, title: 'Members' },
  { num: 3, title: 'Trust Deed' },
  { num: 4, title: 'Registration' },
];

interface Member {
  id: string;
  name: string;
  dob: string;
  tfn: string;
  email: string;
  address: string;
}

const emptyMember = (): Member => ({
  id: crypto.randomUUID(),
  name: '',
  dob: '',
  tfn: '',
  email: '',
  address: '',
});

export default function SMSFNewFundWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1
  const [fundName, setFundName] = useState('');
  const [trusteeType, setTrusteeType] = useState('');
  const [establishmentDate, setEstablishmentDate] = useState('');

  // Step 2
  const [members, setMembers] = useState<Member[]>([emptyMember()]);

  // Step 3
  const [deedProvider, setDeedProvider] = useState('');
  const [deedUploaded, setDeedUploaded] = useState(false);

  // Step 4
  const [abnStatus, setAbnStatus] = useState('not-applied');
  const [tfnStatus, setTfnStatus] = useState('not-applied');
  const [bankName, setBankName] = useState('');
  const [bsb, setBsb] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [esa, setEsa] = useState('');

  const addMember = () => {
    if (members.length >= 6) return;
    setMembers([...members, emptyMember()]);
  };

  const removeMember = (id: string) => {
    if (members.length <= 1) return;
    setMembers(members.filter(m => m.id !== id));
  };

  const updateMember = (id: string, field: keyof Member, value: string) => {
    setMembers(members.map(m => (m.id === id ? { ...m, [field]: value } : m)));
  };

  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      // Step 1: Create the fund
      const fundRes = await apiClient.post('/smsf/funds', {
        fund_name: fundName,
        trustee_type: trusteeType,
        establishment_date: establishmentDate,
        abn: abnStatus === 'received' ? 'pending' : undefined,
        tfn: tfnStatus === 'received' ? 'pending' : undefined,
        bank_name: bankName,
        bsb,
        account_number: accountNumber,
        esa,
      });
      const fundId = fundRes.data?.data?.id;

      if (fundId) {
        // Step 2: Add members
        for (const m of members) {
          if (m.name) {
            await apiClient.post(`/smsf/funds/${fundId}/members`, {
              name: m.name,
              date_of_birth: m.dob,
              tfn: m.tfn,
              email: m.email,
              address: m.address,
            });
          }
        }

        // Step 3: Trust deed info
        if (deedProvider) {
          await apiClient.post(`/smsf/funds/${fundId}/trust-deeds`, {
            provider: deedProvider,
            uploaded: deedUploaded,
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['smsf-funds'] });
      queryClient.invalidateQueries({ queryKey: ['smsf-dashboard'] });

      toast.success('SMSF fund registration completed successfully!', {
        description: `${fundName || 'New Fund'} has been set up. You can now manage it from the dashboard.`,
      });
      navigate('/panel/smsf');
    } catch (err: any) {
      toast.error('Failed to register fund', {
        description: err?.response?.data?.message || err?.message || 'Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <PageHeader
        icon={Shield}
        title="Register New SMSF"
        description="Step-by-step fund setup wizard"
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/smsf')}>
            Cancel
          </Button>
        }
      />

      {/* Step Indicator */}
      <div className="flex items-center justify-between px-2">
        {STEPS.map((s, i) => (
          <div key={s.num} className="flex items-center flex-1">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors',
                  step > s.num
                    ? 'bg-[#5048e5] border-[#5048e5] text-white'
                    : step === s.num
                    ? 'border-[#5048e5] text-[#5048e5] bg-[#5048e5]/10'
                    : 'border-muted-foreground/30 text-muted-foreground'
                )}
              >
                {step > s.num ? <Check className="h-4 w-4" /> : s.num}
              </div>
              <span
                className={cn(
                  'text-sm font-medium hidden sm:inline',
                  step >= s.num ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {s.title}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-3',
                  step > s.num ? 'bg-[#5048e5]' : 'bg-muted-foreground/20'
                )}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Step {step} of 4: {STEPS[step - 1].title}
          </CardTitle>
          <CardDescription>
            {step === 1 && 'Enter the basic details for the new SMSF.'}
            {step === 2 && 'Add fund members (1-6 members allowed).'}
            {step === 3 && 'Upload the trust deed for this fund.'}
            {step === 4 && 'Complete ABN/TFN registration and bank account setup.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Step 1: Fund Details */}
          {step === 1 && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="fundName">Fund Name</Label>
                <Input
                  id="fundName"
                  placeholder="e.g. Smith Family Super Fund"
                  value={fundName}
                  onChange={e => setFundName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Trustee Type</Label>
                <Select value={trusteeType} onValueChange={setTrusteeType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trustee type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual Trustee</SelectItem>
                    <SelectItem value="corporate">Corporate Trustee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="estDate">Establishment Date</Label>
                <Input
                  id="estDate"
                  type="date"
                  value={establishmentDate}
                  onChange={e => setEstablishmentDate(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Step 2: Members */}
          {step === 2 && (
            <>
              {members.map((m, idx) => (
                <div key={m.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Member {idx + 1}</h4>
                    {members.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeMember(m.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Full Name</Label>
                      <Input
                        placeholder="Full name"
                        value={m.name}
                        onChange={e => updateMember(m.id, 'name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={m.dob}
                        onChange={e => updateMember(m.id, 'dob', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>TFN</Label>
                      <Input
                        placeholder="123 456 789"
                        value={m.tfn}
                        onChange={e => updateMember(m.id, 'tfn', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={m.email}
                        onChange={e => updateMember(m.id, 'email', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Address</Label>
                    <Input
                      placeholder="Street address, suburb, state, postcode"
                      value={m.address}
                      onChange={e => updateMember(m.id, 'address', e.target.value)}
                    />
                  </div>
                </div>
              ))}
              {members.length < 6 && (
                <Button variant="outline" size="sm" onClick={addMember}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add Member
                </Button>
              )}
            </>
          )}

          {/* Step 3: Trust Deed */}
          {step === 3 && (
            <>
              <div className="space-y-1.5">
                <Label>Deed Provider</Label>
                <Select value={deedProvider} onValueChange={setDeedProvider}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select deed provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cleardocs">Cleardocs</SelectItem>
                    <SelectItem value="dba-lawyers">DBA Lawyers</SelectItem>
                    <SelectItem value="supercentral">SuperCentral</SelectItem>
                    <SelectItem value="legalsuper">LegalSuper</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Upload Trust Deed</Label>
                <div
                  onClick={() => setDeedUploaded(true)}
                  className={cn(
                    'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                    deedUploaded
                      ? 'border-green-500/50 bg-green-50 dark:bg-green-950/20'
                      : 'border-muted-foreground/30 hover:border-[#5048e5]/50 hover:bg-muted/50'
                  )}
                >
                  {deedUploaded ? (
                    <div className="flex flex-col items-center gap-2 text-green-600">
                      <Check className="h-8 w-8" />
                      <p className="text-sm font-medium">Trust deed uploaded</p>
                      <p className="text-xs text-muted-foreground">trust_deed_v1.pdf</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Upload className="h-8 w-8" />
                      <p className="text-sm font-medium">Click to upload trust deed</p>
                      <p className="text-xs">PDF, DOC, or DOCX up to 10MB</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Step 4: Registration */}
          {step === 4 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>ABN Application Status</Label>
                  <Select value={abnStatus} onValueChange={setAbnStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-applied">Not Applied</SelectItem>
                      <SelectItem value="applied">Applied</SelectItem>
                      <SelectItem value="received">Received</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>TFN Application Status</Label>
                  <Select value={tfnStatus} onValueChange={setTfnStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-applied">Not Applied</SelectItem>
                      <SelectItem value="applied">Applied</SelectItem>
                      <SelectItem value="received">Received</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-medium">Bank Account Details</h4>
                <div className="space-y-1.5">
                  <Label htmlFor="bankName">Bank Name</Label>
                  <Input
                    id="bankName"
                    placeholder="e.g. Commonwealth Bank"
                    value={bankName}
                    onChange={e => setBankName(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="bsb">BSB</Label>
                    <Input
                      id="bsb"
                      placeholder="062-000"
                      value={bsb}
                      onChange={e => setBsb(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="accNum">Account Number</Label>
                    <Input
                      id="accNum"
                      placeholder="1234 5678"
                      value={accountNumber}
                      onChange={e => setAccountNumber(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-1.5">
                <Label htmlFor="esa">Electronic Service Address (ESA)</Label>
                <Input
                  id="esa"
                  placeholder="e.g. BGL Corporate Solutions"
                  value={esa}
                  onChange={e => setEsa(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Required for SuperStream compliance</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(step - 1)}
          disabled={step === 1}
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back
        </Button>

        {step < 4 ? (
          <Button onClick={() => setStep(step + 1)}>
            Next
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        ) : (
          <Button onClick={handleComplete} disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-1.5" />
            )}
            {isSubmitting ? 'Creating Fund...' : 'Complete Setup'}
          </Button>
        )}
      </div>
    </div>
  );
}
